import { useEffect, useId, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';
import { useActiveCompany } from '@/lib/active-company-context';
import { useBackState } from '@/lib/back-nav';
import { useCatalog } from '@/lib/quality-catalogs';

import { EMPTY_DOCUMENT_FORM, useDocumentForm } from '../use-document-form';
import { useRegisterDocument } from '../use-document-registration';
import { useLmsCreationWait, type CreationWait } from '../use-lms-creation-wait';
import { DocumentFields } from './DocumentFields';

// Alta de documento (QA F1 #6/#15, G3-2/G3-3): Calidad elige tipo y proceso, el servidor
// propone el código según 04-GC-P08 (editable) y el documento nace reservado, con su
// dueño de proceso si se eligió. Al reservar se abre su ficha, donde sigue el trabajo.
// Con plantilla, la ficha se abre recién cuando el LMS confirma el documento creado
// (recorrido 2026-11-04): mientras tanto la ventana lo dice, sin atrapar al usuario.

/** Aviso al abrir la ficha, según cómo terminó la creación en el LMS. */
function reservedToast(code: string, wait: CreationWait | null): string {
  if (wait?.state === 'failed') {
    return `Código ${code} reservado, pero el LMS no creó el documento. Revisa «Envíos al LMS» en la ficha.`;
  }
  if (wait?.state === 'timeout' || wait?.state === 'waiting') {
    return `Código ${code} reservado. El LMS sigue creando el documento; la ficha se actualiza sola.`;
  }
  return wait?.fromTemplate === true
    ? `Código ${code} reservado y documento creado en el LMS`
    : `Código ${code} reservado`;
}

export function NewDocumentModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement | null {
  const formId = useId();
  const { activeCompanyId } = useActiveCompany();
  const scope = { companyId: activeCompanyId, enabled: open };
  const types = useCatalog('document-types', scope);
  const areas = useCatalog('process-areas', scope);
  const register = useRegisterDocument();
  const state = useDocumentForm(EMPTY_DOCUMENT_FORM);
  const { showToast } = useToast();
  const navigate = useNavigate();
  // Solo se abre desde la Lista Maestra: la ficha nueva vuelve ahí, con sus filtros.
  const fromList = useBackState('Lista Maestra');
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ id: string; code: string } | null>(null);
  const wait = useLmsCreationWait(created?.id ?? null);
  const finished = useRef(false);
  const { reset } = state;

  // Abre la ficha una sola vez: al confirmarse la creación, al fallar, por tope de espera
  // o porque el usuario no quiso esperar.
  function openDossier(): void {
    if (created === null || finished.current) {
      return;
    }
    finished.current = true;
    showToast(reservedToast(created.code, wait), wait?.state === 'failed' ? 'error' : undefined);
    onClose();
    void navigate(`/documentos/${created.id}`, { state: fromList });
  }

  useEffect(() => {
    if (wait !== null && wait.state !== 'waiting') {
      openDossier();
    }
  });

  useEffect(() => {
    if (open) {
      reset(EMPTY_DOCUMENT_FORM);
      setFormError(null);
      setCreated(null);
      finished.current = false;
    }
  }, [open, reset]);

  const pending = busy || register.isPending;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const valid = state.check();
    if (!state.complete || pending || !valid) {
      return;
    }
    setFormError(null);
    setBusy(true);
    try {
      const entry = await register.mutateAsync(await state.payload());
      setCreated({ id: entry.id, code: entry.code });
    } catch (err) {
      setFormError(state.rejection(err));
    } finally {
      setBusy(false);
    }
  }

  if (created !== null) {
    return (
      <ModalShell
        open={open}
        title="Nuevo documento"
        wide
        onClose={openDossier}
        footer={
          <button type="button" className="btn sm" onClick={openDossier}>
            Ir a la ficha
          </button>
        }
      >
        <p className="modal-body" role="status" aria-busy="true">
          Código {created.code} reservado.{' '}
          {wait?.fromTemplate === true
            ? 'Creando el documento en el LMS desde la plantilla…'
            : 'Abriendo la ficha…'}
        </p>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      open={open}
      title="Nuevo documento"
      wide
      busy={pending}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={pending}>
            Cancelar
          </button>
          <button
            type="submit"
            form={formId}
            className="btn sm"
            disabled={!state.complete || pending}
          >
            {pending ? 'Reservando…' : 'Reservar código'}
          </button>
        </>
      }
    >
      <p className="modal-body">
        El documento queda reservado con este código y nombre; nadie más podrá usar el código.
      </p>
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        <DocumentFields state={state} types={types} areas={areas} />
        {formError && (
          <p className="fld-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
